import { type ReactNode, useEffect, useId, useRef } from 'react';
import { NavLink } from 'react-router';
import { cn } from './cn';

export interface TabItem {
  id: string;
  label: string;
  to: string;
  end?: boolean;
}

/** Tab scorrevoli col pollice; quella attiva è sottolineata col colore della scheda. */
export function ScrollTabs({ items, className }: { items: TabItem[]; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const active = ref.current?.querySelector<HTMLElement>('[aria-current="page"]');
    active?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  });
  return (
    <nav aria-label="Sezioni della scheda" className={cn('relative', className)}>
      <div ref={ref} className="no-scrollbar flex gap-1 overflow-x-auto px-4 lg:px-0">
        {items.map((item) => (
          <NavLink
            key={item.id}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'relative flex h-12 shrink-0 items-center px-3 text-[0.9375rem] whitespace-nowrap transition-colors',
                isActive ? 'font-semibold text-ink' : 'text-ink-2 hover:text-ink',
              )
            }
          >
            {({ isActive }) => (
              <>
                {item.label}
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-x-2 bottom-0 h-[3px] rounded-full transition-opacity',
                    isActive ? 'opacity-100' : 'opacity-0',
                  )}
                  style={{ backgroundColor: 'var(--folder, var(--ink))' }}
                />
              </>
            )}
          </NavLink>
        ))}
      </div>
      <div aria-hidden className="h-px bg-line" />
    </nav>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  label: string;
  className?: string;
}) {
  const name = useId();
  return (
    <fieldset className={cn('inline-flex rounded-[var(--radius-btn)] bg-ink/6 p-1', className)}>
      <legend className="sr-only">{label}</legend>
      {options.map((opt) => {
        const checked = value === opt.value;
        return (
          <label
            key={opt.value}
            className={cn(
              'flex h-9 cursor-pointer items-center rounded-[9px] px-3.5 text-sm font-medium whitespace-nowrap transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-action',
              checked
                ? 'bg-sheet text-ink shadow-[0_1px_2px_rgb(24_36_74/0.12)]'
                : 'text-ink-2 hover:text-ink',
            )}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            {opt.label}
          </label>
        );
      })}
    </fieldset>
  );
}
